import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Download, Loader2, Search, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { getCpcStatsBulk, CpcBulkRow } from '../api/pins';
import PageHeader from '../components/shared/PageHeader';
import SubTabs from '../components/shared/SubTabs';
import FreshBadge from '../components/shared/FreshBadge';
import { Skel } from '../components/shared/Skeleton';
import pathPagesData from '../../path_pages.json';

const CPC_TABS = [
  { label: 'Overview', to: '/cpc/overview'         },
  { label: 'By page',  to: '/cpc',         end: true },
  { label: 'OF vs OT',  to: '/cpc/of-ot' }
];

const ALL_PAGES: string[][] = (pathPagesData as { pages: string[][] }).pages;

const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat('en-US').format(n) : '—';
// Overview screen — 2-decimal precision is enough; raw values stay in CSV.
const fmtCpc = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';

type SortField = 'tags' | 'totalModels' | 'avgCpc' | 'maxCpc' | 'countOf' | 'countOt';
type SortDir = 'asc' | 'desc';

function pageDisplay(tags: string[]): string {
  if (tags.length === 0) return '/best (homepage)';
  return tags.length === 1 ? `/${tags[0]}` : `/${tags.join(' + ')}`;
}

function pageLiveUrl(tags: string[]): string {
  if (tags.length === 0) return 'https://onlyseeker.io/best';
  return `https://onlyseeker.io/best/onlyfans-${tags.join('-')}`;
}

const ROW_HEIGHT = 40;
const LIST_HEIGHT = 600;

// Every column is minmax() so on wide screens the columns absorb the
// extra space proportionally — fixed-px columns were too tight even
// for `$0.05` once cell padding ate 28px per cell. On narrower screens
// the outer .hf-gtable scrolls horizontally past GRID_MIN_WIDTH.
const GRID_COLUMNS = [
  'minmax(200px, 1.6fr)',   // page
  'minmax(160px, 1.3fr)',   // URL
  'minmax(72px,  0.6fr)',   // models
  'minmax(76px,  0.6fr)',   // min
  'minmax(86px,  0.7fr)',   // avg (bold)
  'minmax(76px,  0.6fr)',   // max
  'minmax(64px,  0.5fr)',   // OF #
  'minmax(160px, 1.2fr)',   // OF min/avg/max
  'minmax(64px,  0.5fr)',   // OT #
  'minmax(160px, 1.2fr)',   // OT min/avg/max
  'minmax(110px, 0.9fr)',   // top1
  'minmax(96px,  0.7fr)'    // Detail btn
].join(' ');
const GRID_MIN_WIDTH = 1340; // sum of the minmax mins above

const CpcOverviewPage: React.FC = () => {
  const [rows, setRows] = useState<CpcBulkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('avgCpc');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const CHUNK = 200;
        // "Best" (homepage) — special-page с tags=[], добавляется первой
        // строкой; backend поддерживает empty list как "all PPC".
        const pagesToFetch: string[][] = [[], ...ALL_PAGES];
        const all: CpcBulkRow[] = [];
        for (let i = 0; i < pagesToFetch.length; i += CHUNK) {
          const chunk = pagesToFetch.slice(i, i + CHUNK);
          const r = await getCpcStatsBulk(chunk);
          all.push(...r.pages);
          if (cancelled) return;
        }
        if (!cancelled) setRows(all);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const oldestRefresh = useMemo(() => {
    let oldest: string | null = null;
    for (const r of rows) {
      if (r.refreshedAt && (oldest === null || r.refreshedAt < oldest)) oldest = r.refreshedAt;
    }
    return oldest;
  }, [rows]);

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rows.filter(r => r.tags.some(t => t.toLowerCase().includes(q)))
      : rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = pickSortValue(a, sortField);
      const bv = pickSortValue(b, sortField);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [rows, query, sortField, sortDir]);

  // Measure visible list width so react-window's content matches the parent.
  const listWrapRef = useRef<HTMLDivElement>(null);
  const [listWidth, setListWidth] = useState<number>(0);
  useLayoutEffect(() => {
    const el = listWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setListWidth(e.contentRect.width);
    });
    ro.observe(el);
    setListWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  function toggleSort(f: SortField) {
    if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('desc'); }
  }
  const sortIndicator = (f: SortField) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const exportCsv = () => {
    const header = [
      'page', 'tag_count', 'total_models', 'min_cpc', 'avg_cpc', 'max_cpc',
      'of_count', 'min_cpc_of', 'avg_cpc_of', 'max_cpc_of',
      'ot_count', 'min_cpc_ot', 'avg_cpc_ot', 'max_cpc_ot',
      'top1_username'
    ];
    const lines: string[] = [header.join(',')];
    for (const r of filteredSorted) {
      lines.push([
        '"' + r.tags.join('+') + '"', r.tags.length, r.totalModels,
        r.minCpc ?? '', r.avgCpc ?? '', r.maxCpc ?? '',
        r.countOf, r.minCpcOf ?? '', r.avgCpcOf ?? '', r.maxCpcOf ?? '',
        r.countOt, r.minCpcOt ?? '', r.avgCpcOt ?? '', r.maxCpcOt ?? '',
        r.top1Username ? '"' + r.top1Username + '"' : ''
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cpc-overview-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const actions = (
    <button className="hf-btn" onClick={exportCsv} disabled={rows.length === 0}>
      <Download size={14} /> Export CSV
    </button>
  );

  const Row: React.FC<ListChildComponentProps> = ({ index, style }) => {
    const r = filteredSorted[index];
    return (
      <div className="hf-gtable-row" style={{ ...style, gridTemplateColumns: GRID_COLUMNS, minWidth: GRID_MIN_WIDTH }}>
        <span className="cell hf-mono" title={pageDisplay(r.tags)}>{pageDisplay(r.tags)}</span>
        <span className="cell" title={pageLiveUrl(r.tags)}>
          <a href={pageLiveUrl(r.tags)} target="_blank" rel="noreferrer" className="hf-mono" style={{ color: 'var(--accent)', fontSize: 11 }}>
            {pageLiveUrl(r.tags).replace('https://onlyseeker.io', '')} ↗
          </a>
        </span>
        <span className="cell num">{fmt(r.totalModels)}</span>
        <span className="cell num">{fmtCpc(r.minCpc)}</span>
        <span className="cell num" style={{ fontWeight: 600 }}>{fmtCpc(r.avgCpc)}</span>
        <span className="cell num">{fmtCpc(r.maxCpc)}</span>
        <span className="cell num"><span className="hf-tag outline sm">{r.countOf}</span></span>
        <span className="cell num" style={{ fontSize: 11 }}>
          {r.countOf === 0
            ? <span style={{ color: 'var(--fg-3)' }}>—</span>
            : <>{fmtCpc(r.minCpcOf)} / {fmtCpc(r.avgCpcOf)} / {fmtCpc(r.maxCpcOf)}</>}
        </span>
        <span className="cell num"><span className="hf-tag outline sm">{r.countOt}</span></span>
        <span className="cell num" style={{ fontSize: 11 }}>
          {r.countOt === 0
            ? <span style={{ color: 'var(--fg-3)' }}>—</span>
            : <>{fmtCpc(r.minCpcOt)} / {fmtCpc(r.avgCpcOt)} / {fmtCpc(r.maxCpcOt)}</>}
        </span>
        <span className="cell hf-mono" style={{ fontSize: 11 }}>{r.top1Username ? `@${r.top1Username}` : '—'}</span>
        <span className="cell">
          <Link
            to={`/cpc?pageKey=${encodeURIComponent(r.tags.length === 0 ? 'best' : r.tags.join('-'))}`}
            className="hf-btn ghost sm"
            title="Open By-page drill-down for this page"
          >
            Detail <ChevronRight size={11} />
          </Link>
        </span>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="CPC"
        subtitle={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span><span className="hf-mono">{ALL_PAGES.length.toLocaleString()}</span> pages · OF vs OT CPC split</span>
            {oldestRefresh && <FreshBadge refreshedAt={oldestRefresh} />}
          </span>
        }
        actions={actions}
      />

      <div className="hf-content">
        <SubTabs items={CPC_TABS} />
        <div className="hf-toolbar">
          <div className="hf-input" style={{ minWidth: 280 }}>
            <Search size={14} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="filter by tag (substring match)"
            />
          </div>
          <span className="hf-help" style={{ marginTop: 0, marginLeft: 'auto' }}>
            {loading ? <><Loader2 size={12} style={{ animation: 'hf-spin 1s linear infinite' }} /> loading…</>
                     : <>{filteredSorted.length} / {rows.length} rows</>
            }
          </span>
        </div>

        {error && (
          <div className="hf-banner danger">Error: {error}</div>
        )}

        <div className="hf-card flush">
          <div className="hf-card-header">
            <h3 className="hf-card-title">All pages · sorted by {sortField === 'avgCpc' ? 'avg CPC' : sortField} {sortDir === 'asc' ? '↑' : '↓'}</h3>
            <span className="hf-help" style={{ marginTop: 0 }}>
              {filteredSorted.length.toLocaleString()} of {rows.length.toLocaleString()} pages
            </span>
          </div>
          <div className="hf-gtable" ref={listWrapRef}>
            <div className="hf-gtable-header" style={{ gridTemplateColumns: GRID_COLUMNS, minWidth: GRID_MIN_WIDTH }}>
              <span className="cell sortable" onClick={() => toggleSort('tags')}>page{sortIndicator('tags')}</span>
              <span className="cell">URL</span>
              <span className="cell num sortable" onClick={() => toggleSort('totalModels')}>models{sortIndicator('totalModels')}</span>
              <span className="cell num">min</span>
              <span className="cell num sortable" onClick={() => toggleSort('avgCpc')}>avg{sortIndicator('avgCpc')}</span>
              <span className="cell num sortable" onClick={() => toggleSort('maxCpc')}>max{sortIndicator('maxCpc')}</span>
              <span className="cell num sortable" onClick={() => toggleSort('countOf')}>OF #{sortIndicator('countOf')}</span>
              <span className="cell num">OF min/avg/max</span>
              <span className="cell num sortable" onClick={() => toggleSort('countOt')}>OT #{sortIndicator('countOt')}</span>
              <span className="cell num">OT min/avg/max</span>
              <span className="cell">top1</span>
              <span className="cell"></span>
            </div>

            {loading && rows.length === 0 && (
              Array.from({ length: 10 }).map((_, i) => (
                <div key={`skel-${i}`} className="hf-gtable-row" style={{ gridTemplateColumns: GRID_COLUMNS, minWidth: GRID_MIN_WIDTH, height: ROW_HEIGHT }} aria-hidden="true">
                  {Array.from({ length: 12 }).map((__, j) => (
                    <span key={j} className="cell"><Skel width="70%" /></span>
                  ))}
                </div>
              ))
            )}

            {!loading && filteredSorted.length > 0 && listWidth > 0 && (
              <FixedSizeList
                height={Math.min(LIST_HEIGHT, filteredSorted.length * ROW_HEIGHT)}
                itemCount={filteredSorted.length}
                itemSize={ROW_HEIGHT}
                width={Math.max(listWidth, GRID_MIN_WIDTH)}
                overscanCount={6}
              >
                {Row}
              </FixedSizeList>
            )}

            {!loading && filteredSorted.length === 0 && rows.length > 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--fg-3)' }}>
                No pages match the filter.
              </div>
            )}
          </div>

          {!loading && rows.length > 0 && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--fg-3)', borderTop: '1px solid var(--border)' }}>
              Showing <span className="hf-mono">{filteredSorted.length.toLocaleString()}</span> of <span className="hf-mono">{rows.length.toLocaleString()}</span> pages
            </div>
          )}
        </div>
      </div>
    </>
  );
};

function pickSortValue(r: CpcBulkRow, f: SortField): string | number | null {
  switch (f) {
    case 'tags': return r.tags.join('+');
    case 'totalModels': return r.totalModels;
    case 'avgCpc': return r.avgCpc;
    case 'maxCpc': return r.maxCpc;
    case 'countOf': return r.countOf;
    case 'countOt': return r.countOt;
  }
}

export default CpcOverviewPage;
